import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Wallet, Calculator, Save, Loader2, AlertTriangle, CheckCircle2,
  TrendingUp, TrendingDown, FileText, Sparkles, Banknote, CreditCard,
  Eye, ArrowDownToLine,
} from 'lucide-react';
import api from '../../lib/api';
import ArqueoDetalleModal from './ArqueoDetalleModal';
import { DateRangePicker } from './common/DateRangePicker';

// Spanish locale currency helper: 100.5 → "100,50 €"
const eur = (n) => `${(Number(n) || 0).toFixed(2).replace('.', ',')} €`;

const ArqueoPanel = () => {
  const today = new Date().toISOString().slice(0, 10);

  const [fecha, setFecha] = useState(today);
  const [turno, setTurno] = useState('completo');
  const [fondoInicial, setFondoInicial] = useState('');
  const [efectivoContado, setEfectivoContado] = useState('');
  const [tarjetaContado, setTarjetaContado] = useState('');
  const [gastosEfectivo, setGastosEfectivo] = useState('');
  const [propinas, setPropinas] = useState('');
  const [fianzaDiaSiguiente, setFianzaDiaSiguiente] = useState('');
  const [notas, setNotas] = useState('');
  const [creadoPor, setCreadoPor] = useState('');

  const [resumen, setResumen] = useState(null);
  const [saving, setSaving] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [lastResult, setLastResult] = useState(null);
  // Detail modal state — show full arqueo on row click.
  const [detalleArqueo, setDetalleArqueo] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Live preview of system totals for the selected date
  const fetchResumen = useCallback(async (d) => {
    try {
      const { data } = await api.get(`/admin/ventas/caja/resumen`, { params: { date: d } });
      setResumen(data);
    } catch (e) { /* ignore */ }
  }, []);

  const [histRange, setHistRange] = useState({ from: '', to: '' });
  const fetchHistorial = useCallback(async () => {
    try {
      const params = { limit: 20 };
      if (histRange.from) params.fecha_inicio = histRange.from;
      if (histRange.to) params.fecha_fin = histRange.to;
      const { data } = await api.get(`/admin/ventas/caja/arqueos`, { params });
      setHistorial(data);
    } catch (e) { /* ignore */ }
  }, [histRange]);

  // Last arqueo — used to suggest fondo_inicial from yesterday's fianza.
  const [lastArqueo, setLastArqueo] = useState(null);
  const fetchLastArqueo = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/ventas/caja/arqueos/ultimo`);
      if (data?.found) setLastArqueo(data); else setLastArqueo(null);
    } catch (e) { setLastArqueo(null); }
  }, []);

  useEffect(() => { fetchResumen(fecha); }, [fecha, fetchResumen]);
  useEffect(() => { fetchHistorial(); fetchLastArqueo(); }, [fetchHistorial, fetchLastArqueo]);

  const applyLastFianza = () => {
    if (!lastArqueo) return;
    setFondoInicial(String(lastArqueo.fianza_dia_siguiente ?? 0));
    toast.success(`Fondo precargado con la fianza del ${lastArqueo.fecha}`);
  };

  const num = (v) => parseFloat(v || 0) || 0;
  const efectivoSistema = resumen?.efectivo?.total || 0;
  const tarjetaSistema = resumen?.tarjeta?.total || 0;
  const efectivoEsperado = num(fondoInicial) + efectivoSistema - num(gastosEfectivo);
  const diferencia = +(num(efectivoContado) - efectivoEsperado).toFixed(2);
  // Card reconciliation (only relevant if the user entered a card total).
  const tarjetaContadoNum = tarjetaContado === '' ? null : num(tarjetaContado);
  const diferenciaTarjeta = tarjetaContadoNum === null
    ? null
    : +(tarjetaContadoNum - tarjetaSistema).toFixed(2);
  // Cash to retire from drawer at close (cash counted minus tomorrow's float).
  const fianzaNum = num(fianzaDiaSiguiente);
  const aRetirar = +Math.max(0, num(efectivoContado) - fianzaNum).toFixed(2);

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        fecha,
        turno,
        fondo_inicial: num(fondoInicial),
        efectivo_contado: num(efectivoContado),
        tarjeta_contado: tarjetaContadoNum,
        gastos_efectivo: num(gastosEfectivo),
        propinas: num(propinas),
        fianza_dia_siguiente: fianzaNum,
        notas,
        creado_por: creadoPor,
      };
      const { data } = await api.post(`/admin/ventas/caja/arqueo`, body);
      setLastResult(data);
      toast.success('Arqueo guardado correctamente');
      // Reset entries (keep date)
      setFondoInicial(''); setEfectivoContado(''); setTarjetaContado(''); setGastosEfectivo(''); setPropinas(''); setFianzaDiaSiguiente(''); setNotas('');
      fetchHistorial();
      fetchLastArqueo();
    } catch (e) {
      toast.error('Error al guardar arqueo');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArqueo = async (arqueoId) => {
    if (!window.confirm('¿Eliminar este arqueo? No se puede deshacer.')) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/ventas/caja/arqueos/${arqueoId}`);
      toast.success('Arqueo eliminado');
      setDetalleArqueo(null);
      fetchHistorial();
      fetchLastArqueo();
    } catch (e) {
      toast.error('Error al eliminar el arqueo');
    } finally {
      setDeleting(false);
    }
  };

  const diffColor = Math.abs(diferencia) < 0.5 ? 'text-emerald-600' : diferencia > 0 ? 'text-amber-600' : 'text-red-600';
  const diffBg = Math.abs(diferencia) < 0.5 ? 'bg-emerald-50' : diferencia > 0 ? 'bg-amber-50' : 'bg-red-50';

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="arqueo-panel">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Wallet className="text-amber-600" size={26} />
          <h1 className="text-2xl sm:text-3xl font-light text-gray-800">Arqueo de Caja</h1>
        </div>
        <p className="text-sm text-gray-500">Cierre rápido del turno con cuadre automático e insights de la IA.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* LEFT: Formulario */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardContent className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Fecha</label>
                  <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="arqueo-fecha" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Turno</label>
                  <select value={turno} onChange={(e) => setTurno(e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm" data-testid="arqueo-turno">
                    <option value="completo">Día completo</option>
                    <option value="mañana">Mañana</option>
                    <option value="tarde">Tarde</option>
                    <option value="noche">Noche</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">Tu nombre (opcional)</label>
                <Input value={creadoPor} onChange={(e) => setCreadoPor(e.target.value)} placeholder="Ej: Clara" data-testid="arqueo-creado-por" />
              </div>

              <div className="border-t pt-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Fondo inicial (€)</label>
                    <Input type="number" step="0.01" placeholder="0.00" value={fondoInicial} onChange={(e) => setFondoInicial(e.target.value)} data-testid="arqueo-fondo-inicial" />
                    {lastArqueo && Number(lastArqueo.fianza_dia_siguiente) > 0 && (
                      <button
                        type="button"
                        onClick={applyLastFianza}
                        className="mt-1 inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:text-emerald-800 hover:underline"
                        data-testid="arqueo-use-last-fianza-btn"
                      >
                        <ArrowDownToLine size={11} />
                        Usar fianza del {lastArqueo.fecha} ({(lastArqueo.fianza_dia_siguiente ?? 0).toFixed(2).replace('.', ',')} €)
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Gastos en efectivo (€)</label>
                    <Input type="number" step="0.01" placeholder="0.00" value={gastosEfectivo} onChange={(e) => setGastosEfectivo(e.target.value)} data-testid="arqueo-gastos" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-700 mb-1 block font-semibold">Efectivo contado real (€)</label>
                    <Input type="number" step="0.01" placeholder="0.00" value={efectivoContado} onChange={(e) => setEfectivoContado(e.target.value)} className="border-amber-400 focus:ring-amber-500" data-testid="arqueo-efectivo-contado" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Propinas (€)</label>
                    <Input type="number" step="0.01" placeholder="0.00" value={propinas} onChange={(e) => setPropinas(e.target.value)} data-testid="arqueo-propinas" />
                  </div>
                </div>

                {/* Card reconciliation + next-day float */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-700 mb-1 flex items-center gap-1 font-semibold">
                      <CreditCard size={12} /> Tarjeta (datáfono) (€)
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Total del datáfono"
                      value={tarjetaContado}
                      onChange={(e) => setTarjetaContado(e.target.value)}
                      className="border-blue-300 focus:ring-blue-500"
                      data-testid="arqueo-tarjeta-contado"
                    />
                    <p className="text-[10px] text-gray-400 mt-0.5">Para cuadrar con sistema. Déjalo vacío si no quieres comprobar.</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-700 mb-1 flex items-center gap-1 font-semibold">
                      <Wallet size={12} /> Fianza mañana (€)
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={fianzaDiaSiguiente}
                      onChange={(e) => setFianzaDiaSiguiente(e.target.value)}
                      className="border-emerald-300 focus:ring-emerald-500"
                      data-testid="arqueo-fianza"
                    />
                    <p className="text-[10px] text-gray-400 mt-0.5">Efectivo que dejas en caja para abrir mañana.</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">Notas (incidencias, vales, etc.)</label>
                <Textarea rows={2} value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Cualquier incidencia…" data-testid="arqueo-notas" />
              </div>

              <Button onClick={handleSave} disabled={saving} className="w-full bg-amber-600 hover:bg-amber-700 text-white h-11" data-testid="arqueo-save-btn">
                {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                Guardar arqueo
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* RIGHT: Cuadre + IA */}
        <div className="space-y-4">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Calculator className="text-amber-600" size={18} />
                <h2 className="font-semibold text-gray-800">Cuadre del día</h2>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between text-gray-600">
                  <span className="flex items-center gap-1.5"><Banknote size={14} /> Efectivo sistema</span>
                  <span className="font-mono">{eur(efectivoSistema)}</span>
                </div>
                <div className="flex items-center justify-between text-gray-600">
                  <span className="flex items-center gap-1.5"><CreditCard size={14} /> Tarjeta sistema</span>
                  <span className="font-mono">{eur(tarjetaSistema)}</span>
                </div>
                <div className="border-t my-2"></div>
                <div className="flex items-center justify-between text-gray-600">
                  <span>+ Fondo inicial</span>
                  <span className="font-mono">{eur(num(fondoInicial))}</span>
                </div>
                <div className="flex items-center justify-between text-gray-600">
                  <span>− Gastos efectivo</span>
                  <span className="font-mono">{eur(num(gastosEfectivo))}</span>
                </div>
                <div className="flex items-center justify-between font-semibold text-gray-800">
                  <span>= Efectivo esperado</span>
                  <span className="font-mono">{eur(efectivoEsperado)}</span>
                </div>
                <div className="flex items-center justify-between text-gray-600">
                  <span>Efectivo contado</span>
                  <span className="font-mono">{eur(num(efectivoContado))}</span>
                </div>

                <div className={`mt-3 rounded-lg p-3 ${diffBg}`} data-testid="arqueo-diferencia">
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-semibold ${diffColor}`}>DIFERENCIA EFECTIVO</span>
                    <span className={`font-mono font-bold text-lg ${diffColor}`}>
                      {diferencia > 0 ? '+' : ''}{eur(diferencia)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {Math.abs(diferencia) < 0.5 ? 'Cuadre perfecto' : diferencia > 0 ? 'Sobrante de caja' : 'Faltante de caja'}
                  </p>
                </div>

                {/* Card reconciliation preview */}
                {diferenciaTarjeta !== null && (
                  <div
                    className={`mt-2 rounded-lg p-3 border ${Math.abs(diferenciaTarjeta) < 0.5 ? 'bg-emerald-50 border-emerald-200' : diferenciaTarjeta > 0 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}
                    data-testid="arqueo-diferencia-tarjeta"
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-semibold ${Math.abs(diferenciaTarjeta) < 0.5 ? 'text-emerald-600' : diferenciaTarjeta > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                        DIFERENCIA TARJETA
                      </span>
                      <span className={`font-mono font-bold text-lg ${Math.abs(diferenciaTarjeta) < 0.5 ? 'text-emerald-600' : diferenciaTarjeta > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                        {diferenciaTarjeta > 0 ? '+' : ''}{eur(diferenciaTarjeta)}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1">
                      Datáfono: <span className="font-mono">{eur(tarjetaContadoNum)}</span> · Sistema: <span className="font-mono">{eur(tarjetaSistema)}</span>
                    </p>
                  </div>
                )}

                {/* Float / cash to retire */}
                {fianzaNum > 0 && (
                  <div className="mt-2 rounded-lg p-3 border border-emerald-200 bg-emerald-50/60" data-testid="arqueo-fianza-preview">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-emerald-700 font-semibold flex items-center gap-1"><Wallet size={12} /> FIANZA MAÑANA</span>
                      <span className="font-mono font-bold text-emerald-700">{eur(fianzaNum)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs mt-1">
                      <span className="text-gray-600">A retirar de la caja:</span>
                      <span className={`font-mono font-bold ${num(efectivoContado) < fianzaNum ? 'text-red-600' : 'text-gray-900'}`}>{eur(aRetirar)}</span>
                    </div>
                    {num(efectivoContado) < fianzaNum && num(efectivoContado) > 0 && (
                      <p className="text-[11px] text-red-600 mt-1">⚠️ La fianza supera el efectivo contado.</p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* AI insights from last save */}
          {lastResult && (
            <Card className="border-amber-200 bg-gradient-to-br from-amber-50/60 to-white">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="text-amber-600" size={18} />
                  <h2 className="font-semibold text-gray-800">Insights de la IA</h2>
                </div>
                <div className="space-y-2">
                  {lastResult.insights?.map((ins, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-gray-700" data-testid={`arqueo-insight-${i}`}>
                      {ins.tipo === 'ok' && <CheckCircle2 className="text-emerald-500 mt-0.5 flex-shrink-0" size={16} />}
                      {ins.tipo === 'sobrante' && <TrendingUp className="text-amber-500 mt-0.5 flex-shrink-0" size={16} />}
                      {ins.tipo === 'faltante' && <TrendingDown className="text-red-500 mt-0.5 flex-shrink-0" size={16} />}
                      {ins.tipo === 'alerta' && <AlertTriangle className="text-amber-600 mt-0.5 flex-shrink-0" size={16} />}
                      {ins.tipo === 'info' && <Sparkles className="text-amber-500 mt-0.5 flex-shrink-0" size={16} />}
                      <span>{ins.msg}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Historial */}
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="text-gray-500" size={18} />
          <h2 className="font-semibold text-gray-800">{histRange.from || histRange.to ? 'Arqueos del periodo' : 'Últimos arqueos'}</h2>
        </div>
        <div className="mb-3">
          <DateRangePicker value={histRange} onChange={setHistRange} onClear={() => setHistRange({ from: '', to: '' })} idPrefix="arqueo-range" />
        </div>
        {historial.length === 0 ? (
          <p className="text-sm text-gray-400 italic py-4">{histRange.from || histRange.to ? 'No hay arqueos en estas fechas.' : 'Aún no has guardado ningún arqueo.'}</p>
        ) : (
          <div className="rounded-lg border border-gray-100 overflow-hidden">
            {/* Grid header — replaces <table>/<tbody> to avoid hydration warnings */}
            <div className="hidden sm:grid grid-cols-12 gap-2 bg-gray-50 text-gray-500 text-xs font-medium px-3 py-2 border-b border-gray-100">
              <div className="col-span-2">Fecha</div>
              <div className="col-span-1">Turno</div>
              <div className="col-span-2 text-right">Efectivo</div>
              <div className="col-span-2 text-right">Tarjeta</div>
              <div className="col-span-1 text-right">Total</div>
              <div className="col-span-2 text-right">Diferencia</div>
              <div className="col-span-2 text-left">Por</div>
            </div>
            <div className="divide-y divide-gray-100">
              {historial.map((a) => (
                <button
                  type="button"
                  key={a.id}
                  onClick={() => setDetalleArqueo(a)}
                  className="w-full grid grid-cols-2 sm:grid-cols-12 gap-2 items-center px-3 py-3 text-left text-sm hover:bg-green-50 transition-colors"
                  data-testid={`arqueo-row-${a.id}`}
                >
                  <div className="col-span-2 sm:col-span-2 font-medium">{a.fecha}</div>
                  <div className="col-span-2 sm:col-span-1">
                    <Badge variant="outline" className="text-xs capitalize">{a.turno}</Badge>
                  </div>
                  <div className="col-span-1 sm:col-span-2 sm:text-right font-mono">{eur(a.efectivo_sistema)}</div>
                  <div className="col-span-1 sm:col-span-2 sm:text-right font-mono">{eur(a.tarjeta_sistema)}</div>
                  <div className="col-span-1 sm:col-span-1 sm:text-right font-mono font-semibold">{eur(a.total_sistema)}</div>
                  <div className={`col-span-1 sm:col-span-2 sm:text-right font-mono font-semibold ${Math.abs(a.diferencia) < 0.5 ? 'text-emerald-600' : a.diferencia > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                    {a.diferencia > 0 ? '+' : ''}{eur(a.diferencia)}
                  </div>
                  <div className="col-span-2 sm:col-span-2 text-gray-500 flex items-center justify-between gap-2">
                    <span className="truncate">{a.creado_por}</span>
                    <Eye size={14} className="text-gray-400 flex-shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {detalleArqueo && (
        <ArqueoDetalleModal
          arqueo={detalleArqueo}
          onClose={() => setDetalleArqueo(null)}
          onDelete={handleDeleteArqueo}
          deleting={deleting}
        />
      )}
    </div>
  );
};

export default ArqueoPanel;
